import { DatePipe, NgClass, AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatList } from '@angular/material/list';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { PageToolbarComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';

import { Subject } from 'rxjs';

import { filter, takeUntil } from 'rxjs/operators';

import { CowListItem } from 'src/app/dairyresults/models/production.model';
import { CowDetailsDialogComponent } from 'src/app/shared/components/cow-details-dialog/cow-details-dialog.component';
import { Production } from 'src/app/shared/models/production.model';

import { SomaticCellCountCow } from '../../models/health.model';
import * as HealthActions from '../../store/actions/health.actions';
import { getSomaticCellCountAttentions } from './../../store/selectors/health.selectors';
import { ProductionPickerComponent } from '../../../shared/components/production-picker/production-picker.component';

@Component({
  selector: 'app-somatic-cell-count-attentions',
  templateUrl: './somatic-cell-count-attentions.component.html',
  styleUrls: ['./somatic-cell-count-attentions.component.scss'],
  imports: [
    PageToolbarComponent,
    ProductionPickerComponent,
    MatSlideToggle,
    FormsModule,
    MatList,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatButton,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NotificationEmptyComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class SomaticCellCountAttentionsComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LOCALE_ID);
  private readonly dialogService = inject(DialogService);
  private readonly localeService = inject(LocaleService);

  @ViewChild(MatSort) sort = new MatSort();

  public columns: { id: string; label: string; isLink?: boolean }[] = [
    { id: 'number', label: `health.individual-health.somatic-cell-count-attentions-table.number` },
    { id: 'parity', label: `health.individual-health.somatic-cell-count-attentions-table.parity` },
    {
      id: 'lactationDays',
      label: `health.individual-health.somatic-cell-count-attentions-table.lactation-days`,
    },
    {
      id: 'fatPercentage',
      label: `health.individual-health.somatic-cell-count-attentions-table.fat-percentage`,
    },
    {
      id: 'proteinPercentage',
      label: `health.individual-health.somatic-cell-count-attentions-table.protein-percentage`,
    },
    { id: 'isk', label: `health.individual-health.somatic-cell-count-attentions-table.isk` },
    {
      id: 'lactation',
      label: `health.individual-health.somatic-cell-count-attentions-table.lactation`,
    },
    {
      id: 'attentionCount',
      label: `health.individual-health.somatic-cell-count-attentions-table.attention-count`,
    },
  ];
  public dateColumns: { id: string; label: string; isLink?: boolean }[] = [];
  public dataSource = new MatTableDataSource<SomaticCellCountCow>();
  public showOnlyAttentions = true;
  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));

  private getSomaticCellCountAttentions$ = this.store.select(getSomaticCellCountAttentions);
  private readonly destroy$ = new Subject<void>();
  private cowList: SomaticCellCountCow[] = [];

  ngOnInit() {
    this.store.dispatch(
      SiteActions.updatePageTitle({ pageTitle: 'health.individual-health.title' }),
    );

    this.getSomaticCellCountAttentions$
      .pipe(
        filter((somaticCellCountCows) => !!somaticCellCountCows),
        takeUntil(this.destroy$),
      )
      .subscribe((somaticCellCountCows) => {
        this.dateColumns = [];
        if (somaticCellCountCows.length > 0) {
          const somaticCellCounts = [...somaticCellCountCows[0].somaticCellCounts];
          somaticCellCounts.sort((a, b) => (a.date < b.date ? 1 : -1));
          somaticCellCounts.forEach((somaticCellCount) => {
            this.dateColumns.push({
              id: somaticCellCount.date.toString(),
              label: new DatePipe(this.locale).transform(
                new Date(somaticCellCount.date),
                this.localeService.translate(
                  'health.individual-health.somatic-cell-count-attentions-table.date',
                ),
              ),
            });
          });
        }

        this.cowList = somaticCellCountCows.map((cow) => {
          cow.somaticCellCounts.forEach((somaticCellCount) => {
            cow = { ...cow, [somaticCellCount.date]: somaticCellCount.value };
          });

          return cow;
        });
        this.setDataSource();
      });
  }
  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  public findColumn = (id: string) => this.columns.find((i) => i.id === id);
  public columnIds = (): string[] => this.columns.map((item) => item.id);
  public dateColumnIds = (): string[] => this.dateColumns.map((item) => item.id);
  public displayedColumnIds = (): string[] => {
    const columns = this.columns.map((item) => item.id);
    this.dateColumnIds().forEach((item) => {
      columns.splice(7, 0, item);
    });
    columns.splice(1, 0, 'name');
    return columns;
  };

  public onProductionChanged(production: Production): void {
    this.store.dispatch(
      HealthActions.loadSomaticCellCountAttentions({
        date: new Date(production.date).toISOString(),
      }),
    );
  }

  public setAttention(columnId: string, value: SomaticCellCountCow): string {
    const somaticCellCount = value.somaticCellCounts.find((i) => i.date === columnId);
    return somaticCellCount?.isAttention ? 'attention' : '';
  }

  public openCowDetails({ internationalCowId }: CowListItem): void {
    this.dialogService
      .fullscreenDialog(CowDetailsDialogComponent, {
        internationalCowId,
        title: this.localeService.translate('dairyresults.cow-details.title'),
        setPendingChanges: false,
        fullscreen: true,
      })
      .subscribe(({ status }) => {
        // es-lint-disable-next-line @typescript-eslint/no-unused-expressions
        if (status === DIALOG_RETURN_TYPES.confirm) {
          // Handle the confirmation status but do nothing
        }
      });
  }

  public showOnlyAttentionsChange(event: MatSlideToggleChange) {
    this.showOnlyAttentions = event.checked;
    this.setDataSource();
  }

  public hasData = (): boolean => this.dataSource.data.length > 0;

  private setDataSource() {
    if (this.showOnlyAttentions) {
      this.dataSource.data = this.cowList.filter((cow) => cow.attentionCount > 0);
    } else {
      this.dataSource.data = this.cowList;
    }

    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (
      cow: SomaticCellCountCow,
      sortHeaderId: string,
    ): string | number => {
      const column = this.columns.find((c) => c.id === sortHeaderId);
      if (column !== undefined) {
        return cow[column.id] as string;
      }

      return cow[sortHeaderId] as string;
    };
  }
}
